import React, { useEffect, useCallback, useState } from 'react'
import CssBaseline from '@mui/material/CssBaseline'
import axios from 'axios'
import platform from "platform";

import rubyTheme from './theme'
import jadeTheme from './jade-theme'
import royalTheme from './royal-theme'
import hazelnutTheme from './hazelnut-theme'
import { ThemeProvider } from '@mui/material/styles'
import { trackEvent, initializeGoogleAnalytics } from './util'

const {
  REACT_APP_SHOP_URL
} = process.env;

function useProductData(apiKey: string, config: any) {
  const [isLoading, setIsLoading] = useState(true)
  const [theme, setTheme] = useState(rubyTheme)
  const [themeData, setThemeData] = useState<any>({})

  const getProductData = useCallback(async () => {
    try {
      if (apiKey) {
        const { data } = await axios.get(
          `${REACT_APP_SHOP_URL}/api/v1/shop/client/get?apiKey=${apiKey}`
        )

        if (data) {
          await trackEvent(apiKey!, {
            pageVisit: {
              deviceBrowser: platform.name,
              deviceMobile: platform.product,
              deviceOs: platform.os?.toString(),
            }
          })

          setThemeData({
            ...data,
            apiKey,
            appConfig: config,
          });

          if (
            Array.isArray(data?.googleAnalyticsTrackingId) &&
            data?.googleAnalyticsTrackingId?.length
          ) {
            initializeGoogleAnalytics(data?.googleAnalyticsTrackingId);
          }

          switch (data?.attributes?.theme) {
            case 'red':
              setTheme({
                ...rubyTheme,
                palette: {
                  ...rubyTheme?.palette,
                  primary: {
                    ...rubyTheme?.palette?.primary,
                    main: data?.customization?.primaryColor || rubyTheme?.palette?.primary?.main,
                  }
                }
              })
              break
            case 'blue':
              setTheme({
                ...royalTheme,
                palette: {
                  ...royalTheme?.palette,
                  primary: {
                    ...royalTheme?.palette?.primary,
                    main: data?.customization?.primaryColor || royalTheme?.palette?.primary?.main,
                  }
                }
              })
              break
            case 'brown':
              setTheme({
                ...hazelnutTheme,
                palette: {
                  ...hazelnutTheme?.palette,
                  primary: {
                    ...hazelnutTheme?.palette?.primary,
                    main: data?.customization?.primaryColor || hazelnutTheme?.palette?.primary?.main,
                  }
                }
              })
              break
            case 'green':
              setTheme({
                ...jadeTheme,
                palette: {
                  ...jadeTheme?.palette,
                  primary: {
                    ...jadeTheme?.palette?.primary,
                    main: data?.customization?.primaryColor || jadeTheme?.palette?.primary?.main,
                  }
                }
              })
              break
            default:
              throw new Error('Invalid Product')
          }
          setIsLoading(false)
        } else {
          throw new Error('Invalid Api Key')
        }
      } else {
        throw new Error('Invalid api key')
      }
    } catch (err) {
      setIsLoading(false)
      // navigate('/404')
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [])

  useEffect(() => {
    getProductData()
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [getProductData])

  return { isLoading, theme, themeData }
}

export default function withRoot<P>(Component: React.ComponentType<P>, apiKey: string, config: any) {
  const WithRoot = (props: P) => {
    const { isLoading, theme, themeData } = useProductData(apiKey, config)

    const renderPage = () => (
      <ThemeProvider
        theme={{
          ...theme,
          palette: {
            ...theme?.palette,
            primary: {
              main: themeData?.customization?.primaryColor || theme?.palette?.primary?.main,
            },
          },
          custom: {
            ...themeData,
          },
        }}
      >
        <CssBaseline />
        <Component {...props} test="" />
      </ThemeProvider>
    )

    return isLoading ? null : renderPage()
  }
  return WithRoot
}
